import { useCallback, useEffect, useMemo, useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { useFormik } from 'formik'

import { PaymentHistoryStatuses, PaymentStatuses, PaymentTypes } from 'services/api/ProfileApi/types/types'

import { useAppTranslation } from 'shared/hooks/useAppTranslation'

import { getPayHistory } from 'features/profile/actions'
import { selectProfileCommunication, selectProfileInfo } from 'features/profile/selectors'

import { usePagination } from 'components/UIKit'
import { getSelectOptionValue, SelectOption } from 'components/UIKit/Select'

export type TransactionHistoryFilters = {
  periodStart: Date
  periodEnd: Date
  type?: SelectOption
  status?: SelectOption
}

export const SHOW_CANCEL_BY_STATUS: PaymentStatuses[] = [
  PaymentStatuses.MANUAL_APPROVING_REQUIRED,
  PaymentStatuses.MANUAL_PROCESS_REQUIRED,
]

export const PAYMENT_SUCCESS_STATUSES: PaymentStatuses[] = [
  PaymentStatuses.SUCCESS,
  PaymentStatuses.APPROVING,
  PaymentStatuses.APPROVING_SUCCESS,
]

export const PAYMENT_FAILED_STATUSES: PaymentStatuses[] = [PaymentStatuses.FAILED, PaymentStatuses.APPROVING_FAILED]

export const PAYMENT_IN_PROCESS_STATUSES: PaymentStatuses[] = [
  PaymentStatuses.STARTED,
  PaymentStatuses.MANUAL_APPROVING_REQUIRED,
  PaymentStatuses.MANUAL_PROCESS_REQUIRED,
]

export const PAYMENT_STATUSES_BY_HISTORY_STATUS: Record<PaymentHistoryStatuses, PaymentStatuses[]> = {
  [PaymentHistoryStatuses.SUCCESS]: PAYMENT_SUCCESS_STATUSES,
  [PaymentHistoryStatuses.FAILED]: PAYMENT_FAILED_STATUSES,
  [PaymentHistoryStatuses.IN_PROCESS]: PAYMENT_IN_PROCESS_STATUSES,
  [PaymentHistoryStatuses.FROZEN]: [PaymentStatuses.FROZEN],
  [PaymentHistoryStatuses.EXPIRED]: [PaymentStatuses.EXPIRED],
  [PaymentHistoryStatuses.CANCELED]: [PaymentStatuses.CANCELED],
}

export const PAYMENT_TYPE_OPTIONS = ['all', ...Object.values(PaymentTypes)].map(paymentType => ({
  value: paymentType,
  label: `payment.transactionTypes.${paymentType}`,
}))

export const PAYMENT_STATUSES_OPTIONS = ['all', ...Object.values(PaymentHistoryStatuses)].map(paymentType => ({
  value: paymentType,
  label: `payment.transactionStatuses.${paymentType}`,
}))

export const useTransactions = () => {
  const dispatch = useDispatch()
  const { t } = useAppTranslation('profile')
  const { count, history } = useSelector(selectProfileInfo, shallowEqual).historyInfo
  const { isLoading, error, isSuccess } = useSelector(selectProfileCommunication, shallowEqual).getPayHistory

  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 10,
    totalItems: count,
  })

  const fetchHistory = useCallback(
    (values: TransactionHistoryFilters, page?: number) => {
      const paymentType = getSelectOptionValue(values.type)
      const statusValue = getSelectOptionValue(values.status)
      const paymentStatus =
        statusValue && statusValue !== 'all'
          ? PAYMENT_STATUSES_BY_HISTORY_STATUS[statusValue as PaymentHistoryStatuses]?.join(',')
          : undefined

      dispatch(
        getPayHistory({
          page: page ?? pagination.page,
          pageSize: pagination.pageSize,
          type: paymentType === 'all' ? undefined : (paymentType as PaymentTypes),
          status: paymentStatus as PaymentStatuses | undefined,
          dateFrom: values.periodStart,
          dateTo: values.periodEnd,
        }),
      )
    },
    [dispatch, pagination.page, pagination.pageSize],
  )

  const formValuesRef = useRef<TransactionHistoryFilters | null>(null)

  const formik = useFormik<TransactionHistoryFilters>({
    initialValues: {
      periodStart: dayjs(new Date()).startOf('week').subtract(1, 'week').toDate(),
      periodEnd: new Date(),
      type: { ...PAYMENT_TYPE_OPTIONS[0], label: t(PAYMENT_TYPE_OPTIONS[0].label) },
      status: { ...PAYMENT_STATUSES_OPTIONS[0], label: t(PAYMENT_STATUSES_OPTIONS[0].label) },
    },
    onSubmit: values => {
      formValuesRef.current = values

      if (pagination.page !== 1) {
        pagination.onChangePage(1)
      }

      fetchHistory(values, 1)
    },
  })

  useEffect(() => {
    formValuesRef.current = formik.values
  }, [formik.values])

  const statusOptions: SelectOption[] = useMemo(
    () => PAYMENT_STATUSES_OPTIONS.map(it => ({ ...it, label: t(it.label) })),
    [t],
  )

  const typeOptions: SelectOption[] = useMemo(
    () => PAYMENT_TYPE_OPTIONS.map(it => ({ ...it, label: t(it.label) })),
    [t],
  )

  useEffect(() => {
    const currentType = formik.values.type
    if (currentType) {
      const translatedLabel = t(`payment.transactionTypes.${currentType.value}`)
      if (currentType.label !== translatedLabel) {
        formik.setFieldValue('type', { ...currentType, label: translatedLabel })
      }
    }

    const currentStatus = formik.values.status
    if (currentStatus) {
      const translatedLabel = t(`payment.transactionStatuses.${currentStatus.value}`)
      if (currentStatus.label !== translatedLabel) {
        formik.setFieldValue('status', { ...currentStatus, label: translatedLabel })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync select labels on locale change only
  }, [t])

  useEffect(() => {
    fetchHistory(formValuesRef.current ?? formik.values)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch only when pagination changes
  }, [fetchHistory, pagination.page])

  return {
    history,
    pagination,
    fetchHistory,
    formik,
    statusOptions,
    typeOptions,
    count,
    isLoading,
    isSuccess,
    error,
  }
}
